import { explainMatrixTarget, getItemEconomics, offerRuleOn } from './evaluateBuyingRules.js';

// Buying Rules panel (spec req 3 + 20260713 matrix): the decision surface, not
// just an alarm. Leads with one recommendation row per item — a money badge
// ("offer ≥/≈/≤ £X" from the condition-adjusted matrix cell), the £1000+
// two-path badge, and the bp_matrix.why rationale (config copy, edited at
// monthly gate reviews — this component owns no editorial text). Blocks and
// warnings follow, with item jump-links. Renders nothing before items exist.
//
// With the offer rule on, the offer already IS the rule's figure, so there is no
// recommendation to make: the panel shows blocks and warnings only, and nothing at
// all on a clean quote.
const STANCE_OP = { push: '≥', careful: '≈', cut: '≤' };
const STANCE_WORD = { push: 'floor', careful: 'target', cut: 'cap' };

export default function BuyingRulesPanel({ hits, configError, onJumpToItem, items = [], itemCategories = {}, itemSubCategories = {}, itemSignals = {}, config = null, ready = false }) {
    if (configError) {
        return (
            <div className="qa-rules card">
                <div className="qa-lab">⚠️ Buying rules</div>
                <div className="qa-error">Buying-rules config failed to load — warnings can&apos;t be evaluated and Quote Ready is blocked. Reload the page.</div>
            </div>
        );
    }
    const showRecs = ready && !!config && items.length > 0 && !offerRuleOn(config);
    if (!showRecs && !hits.length) return null;

    const blocks = hits.filter(h => h.severity === 'block');
    const warns = hits.filter(h => h.severity === 'warn');
    const row = (h) => (
        <div key={`${h.itemIndex}-${h.ruleKey}`} className={`qa-rule-row ${h.severity === 'block' ? 'qa-rule-block' : ''}`}>
            <button className="qa-rule-jump" onClick={() => onJumpToItem(h.itemIndex)}>#{h.itemIndex + 1} {h.itemName}</button>
            <span className="qa-rule-msg">{h.message}</span>
        </div>
    );

    return (
        <div className="qa-rules card">
            <div className="qa-lab">
                {hits.length > 0 ? '⚠️ ' : ''}Buying rules
                {blocks.length > 0 && <> — <span className="qa-error">{blocks.length} blocking</span></>}
                {warns.length > 0 && <>{blocks.length > 0 ? ' · ' : ' — '}{warns.length} warning{warns.length !== 1 ? 's' : ''}</>}
            </div>
            {showRecs && (
                <div className="qa-rules-group">
                    {items.map((it, idx) => (
                        <RecommendationRow key={idx} idx={idx} item={it}
                            mainCategory={itemCategories[idx] || null}
                            subCategory={itemSubCategories[idx] || null}
                            signals={itemSignals[idx] ?? null}
                            config={config} onJumpToItem={onJumpToItem} />
                    ))}
                </div>
            )}
            {blocks.length > 0 && (
                <div className="qa-rules-group">
                    <div className="qa-rules-group-lab qa-error">Blocks Quote Ready</div>
                    {blocks.map(row)}
                </div>
            )}
            {warns.length > 0 && (
                <div className="qa-rules-group">
                    <div className="qa-rules-group-lab">Warnings — confirmation required</div>
                    {warns.map(row)}
                </div>
            )}
        </div>
    );
}

// The recommended offer in whole pounds. Rounding never contradicts the badge's
// own operator: push rounds UP (the advertised offer clears the floor), cut
// rounds DOWN (respects the cap), careful rounds to nearest.
function recommendedOffer(sp, targetPct, stance) {
    if (sp == null || sp === '' || targetPct == null) return null;
    const raw = (Number(sp) * targetPct) / 100;
    if (!Number.isFinite(raw)) return null;
    if (stance === 'push') return Math.ceil(raw);
    if (stance === 'cut') return Math.floor(raw);
    return Math.round(raw);
}

function RecommendationRow({ idx, item, mainCategory, subCategory, signals, config, onJumpToItem }) {
    const ex = explainMatrixTarget(item, subCategory, mainCategory, config, signals);
    const path = getItemEconomics(item, mainCategory, config, subCategory, signals).consignmentPath;
    const offer = recommendedOffer(item.sp, ex.targetPct, ex.stance);
    const sign = ex.conditionPp > 0 ? '+' : '−';
    // Cut cells never render "cap X% = Y%" as if Y were a price to AIM for —
    // a cap is a ceiling on reduced-intake gear, so the phrasing is directional
    // ("stay under"). Floors and targets keep the "= Y%" arithmetic format.
    const condClause = ex.conditionPp !== 0 ? ` ${sign}${Math.abs(ex.conditionPp)}pp ${ex.conditionLabel}` : `, ${ex.conditionLabel}`;
    const math = ex.targetPct == null
        ? 'the target appears once the item is priced'
        : ex.stance === 'cut'
            ? `stay under ${ex.targetPct}% — reduced intake (cap ${ex.basePct}%${condClause})`
            : `${STANCE_WORD[ex.stance]} ${ex.basePct}%${ex.conditionPp !== 0 ? ` ${sign}${Math.abs(ex.conditionPp)}pp (${ex.conditionLabel})` : ` (${ex.conditionLabel})`} = ${ex.targetPct}%`;
    return (
        <div className="qa-rec-row">
            <button className="qa-rule-jump" onClick={() => onJumpToItem(idx)}>#{idx + 1} {item.name}</button>
            <span className={`badge badge-sm qa-rec qa-rec-${ex.stance}`}>
                {offer != null ? `offer ${STANCE_OP[ex.stance]} £${offer}` : 'no SP yet'}
            </span>
            {path && (
                <span className={`badge badge-sm qa-rec ${path === 'consign' ? 'qa-rec-cut' : 'qa-rec-push'}`}>
                    {path === 'consign' ? 'offer consignment' : 'buy outright'}
                </span>
            )}
            <div className="qa-why-line">
                <div className="qa-why-math">{ex.cellLabel}: {math}</div>
                <div>{ex.why}</div>
                {ex.pathWhy && <div>{ex.pathWhy}</div>}
            </div>
        </div>
    );
}
